/**
 * Fillable custom cake request. Submits to Admin → Messages.
 */

import { FormEvent, useState } from 'react';
import { DEFAULT_CAKE_REQUEST_NOTICE } from './cakeRequestOptions';

type Lists = { sizes: string[]; flavours: string[]; fillings: string[] };

type Props = {
  lists: Lists;
  notice: string;
};

const fieldClass =
  'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent';

export default function CakeRequestForm({ lists, notice }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      const response = await fetch('/api/cake-request', { method: 'POST', body: data });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(json.error || 'Could not send the request. Please call the bakery.');
        return;
      }
      setSubmitted(true);
      form.reset();
    } catch {
      setError('Could not send the request. Please call the bakery.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="cake-request-form">
      <p className="rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
        {notice || DEFAULT_CAKE_REQUEST_NOTICE} Custom cakes need 72 hours. This is a request — Tammy will quote you.
      </p>

      {submitted && (
        <p className="rounded-lg border border-green-200 bg-green-50 p-4 font-semibold text-green-800">
          Thank you! Tammy will contact you with a quote.
        </p>
      )}
      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 p-4 font-semibold text-red-800">{error}</p>
      )}

      <label className="block text-sm font-medium text-gray-700">
        Name *
        <input name="name" required className={`${fieldClass} mt-1`} autoComplete="name" />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Phone *
        <input name="phone" type="tel" required className={`${fieldClass} mt-1`} autoComplete="tel" />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Email *
        <input name="email" type="email" required className={`${fieldClass} mt-1`} autoComplete="email" />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-gray-700">
          Event date
          <input name="eventDate" type="date" className={`${fieldClass} mt-1`} />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Pickup date
          <input name="pickupDate" type="date" className={`${fieldClass} mt-1`} />
        </label>
      </div>
      <label className="block text-sm font-medium text-gray-700">
        Pickup time
        <input name="pickupTime" type="text" placeholder="e.g. 2:00pm" className={`${fieldClass} mt-1`} />
      </label>

      <label className="block text-sm font-medium text-gray-700">
        Size *
        <select name="size" required defaultValue="" className={`${fieldClass} mt-1`}>
          <option value="" disabled>
            Select size
          </option>
          {lists.sizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Flavour *
        <select name="flavour" required defaultValue="" className={`${fieldClass} mt-1`}>
          <option value="" disabled>
            Select flavour
          </option>
          {lists.flavours.map((flavour) => (
            <option key={flavour} value={flavour}>
              {flavour}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Filling
        <select name="filling" defaultValue="" className={`${fieldClass} mt-1`}>
          <option value="">Select filling</option>
          {lists.fillings.map((filling) => (
            <option key={filling} value={filling}>
              {filling}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm font-medium text-gray-700">
        About how many servings?
        <input name="servings" className={`${fieldClass} mt-1`} />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Writing on the cake
        <input name="writing" className={`${fieldClass} mt-1`} />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Custom design notes
        <textarea name="designNotes" rows={4} className={`${fieldClass} mt-1`} />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Allergies or dietary notes
        <textarea name="allergies" rows={2} className={`${fieldClass} mt-1`} />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Design photos (optional — as many as you like)
        <input name="photos" type="file" multiple className={`${fieldClass} mt-1`} />
      </label>
      <label className="block text-sm font-medium text-gray-700">
        Info picture (optional — invitation, colours, screenshot, anything that helps)
        <input name="infoPhoto" type="file" className={`${fieldClass} mt-1`} />
      </label>
      <label className="flex items-start gap-2 text-sm font-medium text-gray-800">
        <input name="noticeAccepted" type="checkbox" required value="on" className="mt-1" />
        I understand custom cakes need 72 hours notice.
      </label>

      <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
        {submitting ? 'Sending…' : 'Send cake request'}
      </button>
    </form>
  );
}
